/* eslint-disable max-len */
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { ZuperDrawerService } from '@zuper/components/zuper-drawer';
import { LocalisationService } from 'app/core/services/localisation/zuper.localisation.service';
import { GenerateFilterOptions } from '../meta-field-selector/meta-field-selector.interface';
import { DispatchBoardService } from '../../../../projects/dispatch-board/src/app/components/dispatch-board/dispatch-board.service';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { CreateColorProfileService } from './create-color-profile.service';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgxTippyDefaultProps } from 'ngx-tippy-wrapper';
import { AdvanceFilterService } from '../quick-filter/advance-filter/advance-filter.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CreateHotToastRef, HotToastService } from '@ngneat/hot-toast';
import { ZuperDialogService } from '@zuper/components/zuper-dialog';

@Component({
  selector: 'create-color-profile',
  templateUrl: './create-color-profile.component.html',
  styleUrls: ['./create-color-profile.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CreateColorProfileComponent implements OnInit, OnChanges {

  @Input() drawerName: string = 'createColorProfileForm';
  @Input() openDrawer: boolean;
  @Output() actionEmitter: EventEmitter<any> = new EventEmitter();

  //Meta Field Selector
  filterOptionsConfig: GenerateFilterOptions = {
    footerClass: '',
    titleClass: 'custom-filter-header',
    headerClass: 'mt-2 overflow-y-none',
    extraClass: 'm-0',
    showSaveFilter: false,
    showConditionSelector: true,
    actionButton: {
      displayKey: 'text.add_condition',
      displayLabel: 'Add Condition'
    }
  };
  filterOptionalValues: any[] = [];
  filterFieldOptions: any[] = [];
  filterDataLoading: boolean = false;
  filterModule: string;

  //General
  state: `${ColorProfileState}` = ColorProfileState.Listing;
  selectedModule: ColorProfileModule = 'JOBS';
  isLoading = {};
  softDisbaleSubmitBtn: boolean = false;
  colorModules = ['JOBS', 'CUSTOMERS', 'ASSETS', 'PROPERTY'];
  colorProfiles = {};
  newColorProfileModule: 'ALL' | ColorProfileModule = 'ALL';
  iconTextColor: string = null;
  iconsAndColors: any;
  iconColorSelectorTippyProps: NgxTippyDefaultProps = {
    allowHTML: true,
    arrow: true,
    theme: 'light',
    hideOnClick: true,
    trigger: 'click',
    interactive: true,
    appendTo: document.body,
    placement: 'bottom-start',
    zIndex: 9999,
    maxWidth: '512',
    onShow: (instance: any): any => {

    },
    onHide: (): any => {
    }
  };
  loadingToastRef: CreateHotToastRef<any>;
  isColorProfileUpdated: boolean = false;
  showNoteBannerInCreatePage: boolean = false;

  //Listing
  allColorProfiles: any[];
  disableToggleState: boolean = false;
  listingEmptyState = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'JOBS': null, 'CUSTOMERS': null, 'ASSETS': null, 'PROPERTY': null
  };

  //Create / Edit
  colorProfileForm: FormGroup;
  colorProfileDetails: any;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  ColorProfileState = ColorProfileState;

  constructor(
    private zuperDrawerService: ZuperDrawerService,
    private localisationService: LocalisationService,
    private dispatchBoardService: DispatchBoardService,
    private dataCollectorService: DataCollectorService,
    private colorProfileService: CreateColorProfileService,
    private utilityService: UtilityService,
    public advanceFilterService: AdvanceFilterService,
    private toastService: HotToastService,
    private zuperDialogService: ZuperDialogService
  ) { }

  ngOnInit(): void {
    this.localisationService.setDefaultLanguage();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('openDrawer' in changes) {
      this.openDrawer = changes?.openDrawer?.currentValue;
      const drawerRef = this.zuperDrawerService.getComponent(this.drawerName);
      if (drawerRef) {
        if (this.openDrawer) {
          drawerRef.open();
        } else {
          drawerRef.close();
        }
      }
    }
  }

  initDependencies(): void {
    this.colorProfileForm = new FormGroup({
      icon: new FormControl(null, Validators.required),
      color: new FormControl(null, Validators.required),
      name: new FormControl('', Validators.required),
      isEnabled: new FormControl(true, Validators.required),
      conditions: new FormControl([], [Validators.required, Validators.minLength(1)])
    });
    if (!this.colorProfiles) {
      this.colorProfiles = {};
    }
    this.colorModules.forEach((modules: any) => {
      this.colorProfiles[modules] = [];
      this.isLoading[modules] = false;
    });
    this.listingEmptyState.JOBS = this.utilityService.getLocaleValue('color_profile.listing.empty_state.job');
    this.listingEmptyState.ASSETS = this.utilityService.getLocaleValue('color_profile.listing.empty_state.asset');
    this.listingEmptyState.CUSTOMERS = this.utilityService.getLocaleValue('color_profile.listing.empty_state.customer');
    this.listingEmptyState.PROPERTY = this.utilityService.getLocaleValue('color_profile.listing.empty_state.property');
    this.getAllColorProfiles();
  }

  // Listing
  getAllColorProfiles(module: ColorProfileModule | string = 'ALL'): Promise<any> {
    return new Promise((resolve, reject) => {
      const colorProfileUrl = `${this.dataCollectorService.baseUrl}/dispatch_board/color_profiles`;
      const params = { page: 1, count: 100 };
      this.setLoadingState(module, 'progress');
      this.dataCollectorService.getData(colorProfileUrl, params).subscribe(
        (response: any) => {
          const colorProfiles: any[] = response?.data || [];
          this.allColorProfiles = colorProfiles;
          if (!colorProfiles?.length && (!module || module === 'ALL')) {
            this.state = ColorProfileState.Empty;
          }
          else {
            this.state = ColorProfileState.Listing;
            this.mountColorProfiles(colorProfiles);
          }
          this.setLoadingState(module, 'complete');
          resolve(colorProfiles);
        },
        (error: any) => {
          this.setLoadingState(module, 'complete');
          resolve([]);
        });
    });
  }

  getColorProfileDetails(colorProfileUid: string): Promise<any> {
    return new Promise((resolve) => {
      if (!colorProfileUid) { resolve(null); }
      const getColorProfileUrl: string = `${this.dataCollectorService.baseUrl}/dispatch_board/color_profiles/${colorProfileUid}`;
      this.dataCollectorService.getData(getColorProfileUrl).subscribe((reponse: any) => {
        resolve(reponse?.data);
      },
        (error: any) => {
          resolve(null);
        });
    });
  }

  updateColorProfileDetailsLocal(module: ColorProfileModule, colorProfileUid: string, updatedColorProfileDetails: any): void {
    if (this.colorProfiles[module]) {
      const colorProfileIndex: number = this.colorProfiles[module].findIndex((colorProfile: any) => colorProfile?.color_profile_uid === colorProfileUid);
      if (colorProfileIndex > -1) {
        this.colorProfiles[module].splice(colorProfileIndex, 1, updatedColorProfileDetails);
      }
    }
  }

  mountColorProfiles(data: any[]): void {
    const colorProfiles = {};
    data.forEach((profile: any) => {
      profile['iconTextColor'] = this.utilityService.getTextColorBasedOnBg(profile?.color);
      switch (profile?.module) {
        case 'JOB': {
          if (!colorProfiles['JOBS']) {
            colorProfiles['JOBS'] = [];
          }
          colorProfiles['JOBS'].push(profile);
          break;
        }
        case 'CUSTOMER': {
          if (!colorProfiles['CUSTOMERS']) {
            colorProfiles['CUSTOMERS'] = [];
          }
          colorProfiles['CUSTOMERS'].push(profile);
          break;
        }
        default: {
          if (profile?.module) {
            if (!colorProfiles[profile?.module]) {
              colorProfiles[profile?.module] = [];
            }
            colorProfiles[profile?.module].push(profile);
          }
        }
      }
    });
    this.colorModules.forEach((modules: any) => {
      if (colorProfiles?.[modules]?.length) {
        colorProfiles[modules] = colorProfiles[modules].sort((one: any, two: any) => one?.display_order - two?.display_order);
      }
    });
    this.colorProfiles = colorProfiles;
  }

  prefillColorProfileDetails(): void {
    if (this.selectedModule) {
      let color: string = null;
      this.iconsAndColors = this.colorProfileService.iconsAndColorsMeta[this.selectedModule];
      if (this.state === ColorProfileState.Create && this.iconsAndColors?.icons?.length && this.iconsAndColors?.colors?.length) {
        color = this.iconsAndColors?.colors[0];
        this.colorProfileForm.get('color').setValue(color);
        this.colorProfileForm.get('icon').setValue(this.iconsAndColors?.icons[0]);
        this.colorProfileForm.get('name').setValue('');
        this.colorProfileForm.get('isEnabled').setValue(true);
      }
      else if (this.state === ColorProfileState.Update && this.colorProfileDetails?.icon && this.colorProfileDetails?.color) {
        color = this.colorProfileDetails?.color;
        this.colorProfileForm.get('color').setValue(color);
        this.colorProfileForm.get('icon').setValue(this.colorProfileDetails?.icon);
        this.colorProfileForm.get('name').setValue(this.colorProfileDetails?.name);
        this.colorProfileForm.get('isEnabled').setValue(this.colorProfileDetails?.is_active);
      }
      this.iconTextColor = this.utilityService.getTextColorBasedOnBg(color);
    }
  }

  changeColorAndIcon(item: 'color' | 'icon', value: string): void {
    this.colorProfileForm.get(item).setValue(value);
    if (item === 'color') {
      this.iconTextColor = this.utilityService.getTextColorBasedOnBg(this.colorProfileForm.get('color').value);
    }
  }

  drop(event: CdkDragDrop<string[]>, module: ColorProfileModule): void {
    if (this.colorProfiles[module]) {
      moveItemInArray(this.colorProfiles[module], event.previousIndex, event.currentIndex);
      if (event.previousIndex !== event.currentIndex) {
        this.reorderColorProfiles(module);
      }
    }
  }

  getFilterOptions(module: ColorProfileModule, filterValues: any[] = []): void {
    let metaFilterurl: string;
    if (module === 'CUSTOMERS') {
      metaFilterurl = `${this.dataCollectorService.baseUrl}/v2/customers/meta/filter`;
    }
    else {
      metaFilterurl = this.dispatchBoardService.getModuleFilters(module.toLowerCase());
    }
    const preloadFilterMeta = this.colorProfileService.filterMeta[module];
    if (preloadFilterMeta?.length) {
      this.filterFieldOptions = preloadFilterMeta;
    }
    else {
      this.filterDataLoading = true;
      this.dataCollectorService.getData(metaFilterurl)
        .subscribe((response: any) => {
          this.filterFieldOptions = response?.data || [];
          this.colorProfileService.filterMeta[module] = response?.data || [];
          this.filterDataLoading = false;
        }, (error: any) => {
          this.filterFieldOptions = [];
          this.filterDataLoading = false;
        });
    }
    filterValues = (filterValues || []).map((filterValue: any) => {
      filterValue['module'] = this.getModuleName(module);
      return filterValue;
    });
    this.filterOptionalValues = filterValues.map((filterValue: any) => {
      if (['IS_EMPTY', 'IS_NOT_EMPTY'].includes(filterValue?.operator)) {
        delete filterValue['value'];
      }
      return filterValue;
    }) || [];
  }

  createNewColorProfileChange(module: 'ALL' | ColorProfileModule = this.newColorProfileModule): void {
    if (module !== 'ALL') {
      this.openCreateOrUpdateColorProfile(module, ColorProfileState.Create);
    }

  }

  openCreateOrUpdateColorProfile(module: ColorProfileModule, state: `${ColorProfileState}`, colorProfileDetails?: any): void {
    this.filterModule = this.getModuleName(module);
    this.selectedModule = module;
    this.state = state;
    this.colorProfileDetails = colorProfileDetails;
    this.getFilterOptions(module, colorProfileDetails?.conditions || []);
    this.showNoteBannerInCreatePage = this.colorProfiles[module]?.length >= 1;
    this.prefillColorProfileDetails();
  }

  //General
  navigateBackFromCreate(): void {
    if (!this.allColorProfiles?.length) {
      this.state = ColorProfileState.Empty;
      this.newColorProfileModule = 'ALL';
    }
    else {
      this.state = ColorProfileState.Listing;
      this.colorProfileDetails = null;
    }
    this.colorProfileForm.reset();
  }

  iconSelectorTrackBy(index: number, value: string): number {
    return index;
  }

  colorProfileListTrackBy(index: number, value: any): number {
    return value?.color_profile_uid;
  }

  setLoadingState(module: 'ALL' | ColorProfileModule | string, state: 'progress' | 'complete'): void {
    if (module === 'ALL') {
      this.colorModules.forEach((moduleName: any) => {
        this.isLoading[moduleName] = state === 'progress' ? true : false;
      });
    }
    else {
      this.isLoading[module] = state === 'progress' ? true : false;
    }
  }

  onChangeFilters($event: any): void {
    this.colorProfileForm.get('conditions').setValue($event?.filterRules || []);
  }

  updateColorProfileStateInListing(module: ColorProfileModule, state: boolean, colorProfileDetails: any): void {
    const colorProfileUid: string = colorProfileDetails?.color_profile_uid;
    if (colorProfileUid) {
      const updateColorProfileUrl: string = `${this.dataCollectorService.baseUrl}/dispatch_board/color_profiles/${colorProfileUid}`;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const payload = { 'color_profile': { 'is_active': state ?? false } };
      this.disableToggleState = true;
      this.loadingToastRef?.close();
      this.loadingToastRef = this.toastService.loading(this.utilityService.getLocaleValue('color_profile.toast.progress.updating', 'Updating Color Profile'));
      this.dataCollectorService.putData(updateColorProfileUrl, payload).subscribe((response: any) => {
        this.isColorProfileUpdated = true;
        this.loadingToastRef.updateMessage(response?.message);
        this.loadingToastRef.updateToast({ type: 'success', duration: 3000, closeStyle: '' });
        this.disableToggleState = false;
        this.getColorProfileDetails(colorProfileUid).then((updatedColorProfileDetails: any) => {
          updatedColorProfileDetails['iconTextColor'] = this.utilityService.getTextColorBasedOnBg(updatedColorProfileDetails?.color);
          if (updatedColorProfileDetails) {
            this.updateColorProfileDetailsLocal(module, colorProfileUid, updatedColorProfileDetails);
          }
        });
      }, (error: any) => {
        this.loadingToastRef.close();
        this.disableToggleState = false;
      });
    }
  }

  deleteColorProfile(colorProfileDetails: any = this.colorProfileDetails): void {
    const colorProfileUid: string = colorProfileDetails?.color_profile_uid;
    if (colorProfileUid) {
      const colorProfileName = colorProfileDetails?.name || '';
      const dialogTitle = this.utilityService.getLocaleValue('color_profile.delete.title', 'Delete Color Profile');
      const dialogMessage = `${this.utilityService.getLocaleValue('color_profile.delete.message', 'Are you sure you want to delete the color profile')} <span class="text-gray-900 font-medium">
      '${colorProfileName}'</span> ?`;
      const confirmationDialog = this.zuperDialogService.open({
        title: dialogTitle,
        type: 'error',
        message: dialogMessage,
        actions: {
          confirm: {
            show: true,
            label: 'Delete',
            color: 'error',
            confirmDisplayKey: 'delete.text',
          },
          cancel: {
            show: true,
            label: 'Cancel',
            cancelDisplayKey: 'dispatchBoard.scheduler.dialog.delete.cancel'
          }
        },
        dismissible: false,
        extraClass: 'color-profile-delete-dialog'
      });
      confirmationDialog.afterClosed().subscribe((status: boolean) => {
        if (status) {
          const deleteLoadinfToastRef = this.toastService.loading(this.utilityService.getLocaleValue('color_profile.toast.progress.deleting', 'Deleting Color Profile'));
          const updateColorProfileUrl: string = `${this.dataCollectorService.baseUrl}/dispatch_board/color_profiles/${colorProfileUid}`;
          this.dataCollectorService.deleteData(updateColorProfileUrl).subscribe((response: any) => {
            this.isColorProfileUpdated = true;
            deleteLoadinfToastRef.close();
            this.toastService.success(response?.message);
            this.allColorProfiles = this.allColorProfiles.filter((profileDetails: any) => profileDetails?.color_profile_uid !== colorProfileUid);
            this.mountColorProfiles(this.allColorProfiles);
            this.navigateBackFromCreate();
          });
        }
      });
    }

  }

  getModuleName(module: ColorProfileModule): ColorProfileModule | string {
    if (module === 'JOBS') {
      return 'JOB';
    }
    else if (module === 'CUSTOMERS') {
      return 'CUSTOMER';
    }
    return module;
  }

  reorderColorProfiles(module: ColorProfileModule): void {
    if (this.colorProfiles[module]?.length) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const colorProfiles = this.colorProfiles[module].map((profileDetails: any, index: number) => ({ 'color_profile_uid': profileDetails?.color_profile_uid, 'display_order': index + 1 }));
      const payload = {
        module: this.getModuleName(module),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        color_profiles: colorProfiles
      };
      const colorProfileReorderUrl: string = `${this.dataCollectorService.baseUrl}/dispatch_board/color_profiles/reorder`;
      this.loadingToastRef = this.toastService.loading(this.utilityService.getLocaleValue('color_profile.toast.progress.reordering', 'Reordering Color Profile'));
      this.dataCollectorService.postData(colorProfileReorderUrl, payload).subscribe((response: any) => {
        this.isColorProfileUpdated = true;
        this.loadingToastRef.updateMessage(response?.message);
        this.loadingToastRef.updateToast({ type: 'success', duration: 3000 });
        this.getAllColorProfiles(module);
      },
        (error: any) => {
          this.loadingToastRef.close();
        });
    }
  }

  submitColorProfileForm(): void {
    this.colorProfileForm.markAllAsTouched();
    let conditions: any[] = [];
    const colorProfileValue: any = this.colorProfileForm.value;
    if (colorProfileValue?.conditions?.length && colorProfileValue?.conditions?.length) {
      conditions = colorProfileValue.conditions.map((condition: any) => {
        if (!condition?.display_name?.length && condition?.displayKeyValue && Array.isArray(condition?.displayKeyValue)) {
          condition['display_name'] = condition?.displayKeyValue;
        }
        return condition;
      });
    }
    if (this.colorProfileForm?.invalid || !conditions?.length) {
      return;
    }
    const payload = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'color_profile': {
        'icon': colorProfileValue?.icon,
        'color': colorProfileValue?.color,
        'name': colorProfileValue?.name,
        'module': this.getModuleName(this.selectedModule),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'is_active': colorProfileValue?.isEnabled,
        'conditions': conditions
      }
    };
    if (this.state === 'CREATE') {
      const createOrUpdateColorProfileUrl: string = `${this.dataCollectorService.baseUrl}/dispatch_board/color_profiles`;
      this.softDisbaleSubmitBtn = true;
      this.loadingToastRef?.close();
      this.loadingToastRef = this.toastService.loading(this.utilityService.getLocaleValue('color_profile.toast.progress.creating', 'Creating Color Profile'));
      this.dataCollectorService.postData(createOrUpdateColorProfileUrl, payload).subscribe((response: any) => {
        this.isColorProfileUpdated = true;
        this.loadingToastRef.updateMessage(response?.message);
        this.loadingToastRef.updateToast({ type: 'success', duration: 3000 });
        this.softDisbaleSubmitBtn = false;
        this.getAllColorProfiles(payload.color_profile.module).then(() => {
          this.navigateBackFromCreate();
        });
      }, (error: any) => {
        this.loadingToastRef.close();
        this.softDisbaleSubmitBtn = false;
      });
    }
    else if (this.state === 'UPDATE' && this.colorProfileDetails?.color_profile_uid) {
      const colorProfileUid: string = this.colorProfileDetails?.color_profile_uid;
      const createOrUpdateColorProfileUrl: string = `${this.dataCollectorService.baseUrl}/dispatch_board/color_profiles/${colorProfileUid}`;
      this.loadingToastRef?.close();
      this.loadingToastRef = this.toastService.loading(this.utilityService.getLocaleValue('color_profile.toast.progress.updating', 'Updating Color Profile'));
      this.dataCollectorService.putData(createOrUpdateColorProfileUrl, payload).subscribe((response: any) => {
        this.loadingToastRef.updateMessage(response?.message);
        this.loadingToastRef.updateToast({ type: 'success', duration: 3000 });
        this.navigateBackFromCreate();
        this.softDisbaleSubmitBtn = false;
        this.setLoadingState(this.selectedModule, 'progress');
        this.getColorProfileDetails(colorProfileUid).then((updatedColorProfileDetails: any) => {
          this.isColorProfileUpdated = true;
          updatedColorProfileDetails['iconTextColor'] = this.utilityService.getTextColorBasedOnBg(updatedColorProfileDetails?.color);
          if (updatedColorProfileDetails) {
            this.updateColorProfileDetailsLocal(this.selectedModule, colorProfileUid, updatedColorProfileDetails);
          }
          this.setLoadingState(this.selectedModule, 'complete');
        });
      }, (error: any) => {
        this.loadingToastRef?.close();
        this.softDisbaleSubmitBtn = false;
      });
    }
  }

  emitAction(event: any): void {
    this.actionEmitter.emit({ ...event, isUpdated: this.isColorProfileUpdated });
  }

  tearDown(): void {
    this.colorProfileForm.reset();
    this.colorProfileForm = null;
    this.colorProfileDetails = null;
    this.isColorProfileUpdated = false;
  }

  //Drawer controls
  drawerOpenedChanged(isOpened: any): void {
    if (isOpened) {
      this.initDependencies();
    }
    else {
      this.newColorProfileModule = 'ALL';
      this.emitAction({ isClosed: true });
      this.openDrawer = false;
      this.tearDown();
    }
  }

  closeDrawer(): void {
    this.openDrawer = false;
    const drawerRef = this.zuperDrawerService.getComponent(this.drawerName);
    if (drawerRef) {
      drawerRef.close();
    }
  }
}

export type ColorProfileModule = 'JOBS' | 'ASSETS' | 'PROPERTY' | 'CUSTOMERS';
export enum ColorProfileState {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Empty = 'EMPTY', Listing = 'LISTING', Create = 'CREATE', Update = 'UPDATE'
}
